import { createContext } from 'react';

/**
 * The type definition for the LoadingContext.
 * @interface LoadingContextType
 * @property {boolean} isLoading The current loading state (true if loading, false otherwise).
 * @property {(source?: string) => void} showLoading A function that increments the loading counter, effectively showing the loading indicator. Optionally takes a source string for logging purposes. It should be unique for every invoke function.
 * @property {(source?: string) => void} hideLoading A function that decrements the loading counter, effectively hiding the loading indicator when no loading operations are active. Optionally takes a source string for logging purposes.
 * @property {boolean} showConsoleLogs A boolean value indicating if console logs should be shown when using the withLoading utility function. If true, logs will be displayed; otherwise, they will be hidden.
 */
export interface LoadingContextType {
  isLoading: boolean;
  showLoading: (source?: string, showConsoleLogs?: boolean) => void;
  hideLoading: (source?: string, showConsoleLogs?: boolean) => void;
  showConsoleLogs: boolean;
}

/**
 * The LoadingContext, which provides the loading state and methods to show and hide the loading indicator, as well as a flag to control console logging in the withLoading utility function.
 * @constant createContext A React context with a default value for isLoading, showLoading, hideLoading, and showConsoleLogs properties.
 * @returns A React context instance that holds the loading state and methods to show and hide the loading indicator, and the showConsoleLogs flag.
 */
const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
  showLoading: () => {},
  hideLoading: () => {},
  showConsoleLogs: false, // Add a default for showConsoleLogs
});

export default LoadingContext;
